import { useState, useEffect, useRef } from 'react';
import CountUp from 'react-countup';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

import { fetchReportData } from '../redux/slices/reportSlice';
import {
  IMG,
  ANIMATED_COUNTERS,
  REPORT_SERVICES,
  REPORT_OPTIONS,
  bookingLinkSrc,
} from '../const';
import { getNumberAsString, getWorkingWeek } from '../helpers';
import Media from '../components/Media';
import Tabs from '../components/report-tabs';
import '../assets/styles/report.scss';
import Testimonials from '../components/testimonials';
import Footer from '../components/footer';
import Loader from '../components/loader';
import Container from '../components/container';
import ErrorMessage from '../components/report-error';
import NotificationEmail from '../components/report-tabs/NotificationEmail';

const Report = () => {
  const { verified } = useSelector((s) => s.form);
  const { reportData, loading, success, failed, error } = useSelector(
    (s) => s.report,
  );
  const dispatch = useDispatch();
  const { id } = useParams();

  const counterBlock = useRef(null);
  const pageRef = useRef(null);

  const [activeOption, setActiveOption] = useState('');
  const [isHiddenButtons, setHiddenButtons] = useState(false);
  const [startCount, setStartCount] = useState(false);
  const [isLinkCopied, setLinkCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [tabButtons, setTabButtons] = useState([]);
  const [designPrice, setDesignPrice] = useState('€3-5,3K');
  const [designTime, setDesignTime] = useState('5');
  const [days, setDays] = useState({ min: 0, max: 0 });
  const [isOpenNotification, setOpenNotification] = useState(false);

  // open notification modal
  useEffect(() => {
    if (verified) setOpenNotification(true);
  }, [verified]);

  // set budget range on tab buttons:
  useEffect(() => {
    const buttonsList = [];
    let priceOfDesign = '';
    let timeOfDesign = '';

    if (reportData.page_id === id) {
      REPORT_OPTIONS.forEach((item) => {
        const match = reportData.results[item.id];
        let newItem;

        if (!match) {
          newItem = item;
        } else {
          newItem = {
            ...item,
            cost: getNumberAsString(+match.price_min, +match.price_max),
          };

          if (match.design_min && match.design_max) {
            priceOfDesign = getNumberAsString(
              +match.design_min,
              +match.design_max,
            );
          }

          // add values to Design tab
          if (match.design_days_min && match.design_days_max) {
            let week = '';

            setDays({
              min: +match.design_days_min,
              max: +match.design_days_max,
            });

            if (match.design_days_min === match.design_days_max) {
              week = getWorkingWeek(+match.design_days_max);
            } else {
              week = `${getWorkingWeek(
                +match.design_days_min,
              )}–${getWorkingWeek(+match.design_days_max)}`;
            }

            if (!timeOfDesign || timeOfDesign != week) {
              timeOfDesign = week;
              setDesignTime(week);
            }
          }
        }

        buttonsList.push(newItem);
      });

      // add priceOfDesign to tab button
      if (priceOfDesign) {
        setDesignPrice(priceOfDesign);
        const index = buttonsList.findIndex((item) => item.id === 'design');

        if (index !== -1) {
          buttonsList[index] = {
            id: 'design',
            value: 'UI/UX design',
            cost: priceOfDesign,
          };
        }
      }

      setTabButtons(buttonsList);
    }
  }, [id, reportData]);

  // fetch report data
  useEffect(() => {
    if (reportData.page_id != id && !loading && isLoading) {
      dispatch(fetchReportData(id));
      setIsLoading(false);
    }
    setIsLoading(false);
  }, [id, loading, success, reportData.page_id, isLoading]);

  useEffect(() => {
    if (!activeOption) setActiveOption(REPORT_OPTIONS[0]);
  }, [activeOption]);

  // start counter, set buttons view
  useEffect(() => {
    const handleStartCount = () => {
      if (counterBlock.current) {
        const counterBlockOffset = counterBlock.current.getBoundingClientRect();
        const counterBlockPosition = counterBlockOffset.top - 500;
        if (!startCount && counterBlockPosition < 0) {
          setStartCount(true);
        }
      }

      // change the appearance of tab buttons when scrolling
      if (pageRef.current) {
        const pageRefOffset = pageRef.current.getBoundingClientRect();
        const pageRefPosition = pageRefOffset.top;

        if (pageRefPosition < -120 && !isHiddenButtons) {
          setHiddenButtons(true);
        } else if (pageRefPosition >= 0 && isHiddenButtons) {
          setTimeout(() => setHiddenButtons(false), 0);
        }
      }
    };

    document.addEventListener('scroll', handleStartCount, { passive: true });

    return () => {
      document.removeEventListener('scroll', handleStartCount);
    };
  }, [startCount, isHiddenButtons]);

  const onSetActive = (e) => {
    const id = e.target.id;
    const currentOption = REPORT_OPTIONS.find((option) => id === option.id);
    if (currentOption) {
      // if (isHiddenButtons) {
      //   document
      //     .querySelector(`#report-tabs--js`)
      //     .scrollIntoView({ behavior: 'smooth' });
      // }

      setActiveOption(currentOption);
    }
  };

  const handleCopyLink = async () => {
    const url = window.location.href;

    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1500);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };

  const handleCloseNotificationModal = () => setOpenNotification(false);

  if (isLoading || loading)
    return (
      <Container>
        <Loader />
      </Container>
    );

  if (!isLoading && !loading && failed) {
    return <ErrorMessage message={error} fromReportPage />;
  }

  return (
    <>
      <div className="bg-wide-sc">
        <main className="report-page" ref={pageRef}>
          <div className="report-page__heading-sticky">
            <div
              className={`report-page__heading${
                isHiddenButtons ? ' btn-hidden' : ''
              }`}
            >
              <div>
                <h1>
                  Check <strong>your estimations</strong>
                </h1>
                <p>We’ve carefully analyzed your responses and calculated</p>
                <div className="copy-link-btn__wrapper">
                  <button
                    type="button"
                    className="copy-link-btn ic-copy-link"
                    onClick={handleCopyLink}
                  >
                    <div
                      className={`copy-link-btn__tooltip${
                        isLinkCopied ? ' copied ic-check' : ''
                      }`}
                    >
                      {isLinkCopied ? 'Link copied' : 'Copy report link'}
                    </div>
                  </button>
                </div>
              </div>
              <div className="report-page__heading__link-wrapper">
                <a
                  className="primaryFatLink ic-consultation"
                  href={bookingLinkSrc}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Book free consultation
                </a>
                <p>to get 100% accuracy estimations</p>
              </div>
            </div>

            <div
              className={`options-fieldset${
                isHiddenButtons ? ' btn-hidden' : ''
              }`}
            >
              <legend>Choose an option:</legend>
              <div className="options-wrapper">
                <div>
                  {tabButtons.length > 0 &&
                    tabButtons.map((option) => (
                      <div key={option.id} className="option-btn-group">
                        <button
                          type="button"
                          id={option.id}
                          className={`ic-check${
                            activeOption.id === option.id ? ' active' : ''
                          }`}
                          onClick={onSetActive}
                        >
                          {option.value}
                          <span>{option.cost}</span>
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>

          <div>
            <section className="report-page__tabs-block" id="report-tabs--js">
              <Tabs
                activeOption={activeOption}
                results={reportData.results}
                haveDesign={reportData.have_design}
                designPrice={designPrice}
                designTime={designTime}
                days={days.min ? days : ''}
              />
            </section>
          </div>

          <section className="services-section">
            <h2>
              Services that <strong>match</strong> your goals
            </h2>
            <p>
              Learn about the services the project may include to align with
              your business goals.
            </p>
            <div className="services-list__wrapper">
              <ul className="services-list">
                {REPORT_SERVICES.map((service) => (
                  <li key={service.id} className="services-list__item">
                    <div>
                      <picture>
                        <source
                          srcSet={`${IMG.QUICK_QUOTE}/${service.image}.avif`}
                          type="image/avif"
                        />
                        <source
                          srcSet={`${IMG.QUICK_QUOTE}/${service.image}.webp`}
                          type="image/webp"
                        />
                        <source
                          srcSet={`${IMG.QUICK_QUOTE}/${service.image}.png`}
                          type="image/png"
                        />
                        <img
                          src={`${service.image}.png`}
                          width="190"
                          height="196"
                          alt={service.title}
                          loading="lazy"
                        />
                      </picture>
                    </div>
                    <div>
                      <h3>{service.title}</h3>
                      <p>{service.text}</p>
                    </div>
                    <a
                      className="services-list__item-link"
                      href={service.link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      See more details
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="about-section">
            <div>
              <h2>
                About our <strong>company</strong>
              </h2>
              <p>
                Let’s develop a software product based on your unique idea and
                our 10+ years of expertise.
              </p>
              <div className="about-section__text">
                <p>
                  At LaSoft, we turn complex product ideas into scalable,
                  intelligent digital solutions.
                </p>
                <p>
                  Our cross-functional teams work with enterprises, SMBs, and
                  startups to empower clients across industries like logistics,
                  healthcare, finance, HR, education, and more.
                </p>
                <p>
                  As an experienced software development partner,
                  we&nbsp;specialize in AI-powered platforms, cloud
                  infrastructure, data visualization, and custom
                  software&nbsp;development.
                </p>
              </div>
            </div>
            <div className="counters-block">
              <ul className="counters-block__list" ref={counterBlock}>
                {ANIMATED_COUNTERS.map((item) => (
                  <li key={item.id}>
                    <div>
                      <strong>
                        {startCount && (
                          <CountUp
                            start={item.start}
                            end={item.end}
                            delay={item.delay}
                            duration={7}
                            useEasing={false}
                            suffix={item.suffix ? item.suffix : ''}
                          >
                            {({ countUpRef }) => <ins ref={countUpRef} />}
                          </CountUp>
                        )}
                      </strong>
                    </div>
                    <span>{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
          <Testimonials />
        </main>
      </div>
      <Footer />
      <Media query="(max-width: 900px)">
        {(match) =>
          match && (
            <>
              <div className="copy-link-btn__wrapper-mobile">
                <button
                  type="button"
                  className="copy-link-btn ic-copy-link"
                  onClick={handleCopyLink}
                >
                  <div
                    className={`copy-link-btn__tooltip${
                      isLinkCopied ? ' copied ic-check' : ''
                    }`}
                  >
                    {isLinkCopied ? 'Link copied' : 'Copy report link'}
                  </div>
                </button>
              </div>
              <div className="booking-link-mobile">
                <a
                  className="primaryFatLink ic-consultation"
                  href={bookingLinkSrc}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Book free consultation
                </a>
              </div>
            </>
          )
        }
      </Media>
      {createPortal(
        <NotificationEmail
          isOpen={isOpenNotification}
          onClose={handleCloseNotificationModal}
        />,
        document.getElementById('root'),
      )}
    </>
  );
};

export default Report;

Report.propTypes = {
  reportData: PropTypes.object,
  loading: PropTypes.bool,
  success: PropTypes.bool,
  failed: PropTypes.bool,
  id: PropTypes.string.isRequired,
};
